import React, { useCallback, useContext, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Typography } from "@mui/material";
import type mapboxgl from "mapbox-gl";

import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { SNAP_INDICATOR_SOURCE } from "fond/draw/snapIndicator";
import { MapContext } from "fond/map/MapProvider";
import {
  DeleteButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { useSnapIndicator } from "fond/map/useSnapIndicator";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const snapTargetLayers: string[] = [MuPowerEditableLayerIds.ugPoints, MuPowerInputLayerIds.poles, MuPowerEditableLayerIds.ugPath];

const MuPowerSourceHubsDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { map } = useContext(MapContext);
  const { clear } = useStackedNavigationContext();

  useSnapIndicator(map);

  const snapGetFeatures = useCallback((m: mapboxgl.Map, draw: MapboxDraw) => {
    const tileFeatures = snapTargetLayers.flatMap((layerKey) => m.querySourceFeatures("project-layers", { sourceLayer: layerKey }));
    return [...draw.getAll().features, ...tileFeatures];
  }, []);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        snap: true,
        snapIndicatorSource: SNAP_INDICATOR_SOURCE,
        snapOptions: {
          snapPx: 25,
          snapVertexPriorityDistance: 0.000025,
          snapGetFeatures,
        },
        editOptions: {
          allLayers: [
            {
              ID: MuPowerSolverLayerIds.sourceHubs,
              GeometryType: "Point",
              getFeatureProperties: () => ({ layerId: MuPowerSolverLayerIds.sourceHubs }),
            },
          ],
          editingLayerId: MuPowerSolverLayerIds.sourceHubs,
          editingLayerIds: [MuPowerSolverLayerIds.sourceHubs],
        },
      }) as unknown as MapboxDrawOptions,
    [snapGetFeatures]
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent
        title="Edit source hubs"
        alertTitle="Draw source hubs"
        alertBody={<Typography variant="body2">Points will snap to underground points, poles, and underground path edges.</Typography>}
        onFinish={clear}
      />
    </MultiLayerEditProvider>
  );
};

export default MuPowerSourceHubsDraw;
